import he from "he";

const LINE_WIDTH = 48;
const divider = "-".repeat(LINE_WIDTH);
const strongDivider = "=".repeat(LINE_WIDTH);

const safeDecode = (value) =>
  typeof value === "string" ? he.decode(value) : "";

const centerText = (text = "") => {
  const clean = String(text).trim();
  if (!clean) return "";
  if (clean.length >= LINE_WIDTH) return clean.slice(0, LINE_WIDTH);
  const leftPad = Math.floor((LINE_WIDTH - clean.length) / 2);
  return `${" ".repeat(leftPad)}${clean}`;
};

const clampLine = (text = "") => String(text).slice(0, LINE_WIDTH);

function formatSeat(item) {
  const seat = item?.PreModifier?.S0 || "";
  const cleaned = safeDecode(seat)
    .replace(/seat\s*#?\s*:?\s*/i, "")
    .trim();

  return cleaned ? `[Seat ${cleaned}]` : "";
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getVisiblePrintItems(items = [], settings = {}) {
  return items.filter((item) => {
    if (!item) return false;
    if (item.isMessage || item.isFireItem || item.isCourseSeparator) return false;
    if (item.isParked || String(item.status || "").toLowerCase() === "parked") return false;

    if (settings?.hide_bumped_on_print && item.isBumped) return false;
    if (settings?.hide_voided_on_print && item.isVoided) return false;

    return true;
  });
}

function getPreModifiers(item) {
  return Object.entries(item?.PreModifier || {})
    .filter(([key]) => key.startsWith("S") && key !== "S0")
    .map(([, val]) => safeDecode(val).trim())
    .filter(Boolean);
}

function combineCondimentModifiers(condiments = [], settings = {}) {
  if (settings.combine_condiments === false) {
    return condiments
      .map((c) => safeDecode(c?.Name || "").trim())
      .filter(Boolean);
  }

  const prefixes = new Set(["NO", "EXTRA", "ONLY", "LIGHT", "ADD"]);
  const suffixes = new Set(["ON THE SIDE"]);

  const toTitleCase = (str) =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const normalize = (str) =>
    str.replace(/^-/, "").trim().toUpperCase().replace(/\s+/g, " ");

  const result = [];

  for (let i = 0; i < condiments.length; i += 1) {
    const current = condiments[i];
    const next = condiments[i + 1];

    const rawCurr = safeDecode(current?.Name || "");
    const rawNext = safeDecode(next?.Name || "");
    const currNorm = normalize(rawCurr);
    const nextNorm = normalize(rawNext);

    if (prefixes.has(currNorm) && next) {
      const cleanNext = rawNext.replace(/^-/, "").trim();
      const combinedText = `${toTitleCase(currNorm)} ${cleanNext}`;

      result.push(combinedText.trim());
      i += 1;
    } else if (next && suffixes.has(nextNorm)) {
      const cleanCurrent = rawCurr.replace(/^-/, "").trim();
      result.push(`${toTitleCase(cleanCurrent)} On The Side`);
      i += 1;
    } else if (suffixes.has(currNorm)) {
      const clean = rawCurr.replace(/^-/, "").trim();
      if (clean) result.push(clean);
    } else if (!prefixes.has(currNorm) && !suffixes.has(currNorm)) {
      const clean = rawCurr.replace(/^-/, "").trim();
      if (clean) result.push(clean);
    }
  }

  return result;
}

function getItemModifiers(item, settings = {}) {
  const preModifiers = getPreModifiers(item);

  const condimentsRaw = Array.isArray(item?.Condiment)
    ? item.Condiment
    : item?.Condiment
      ? [item.Condiment]
      : [];

  const condiments = combineCondimentModifiers(condimentsRaw, settings);

  return [...preModifiers, ...condiments].filter(Boolean);
}

function groupItems(items = [], settings = {}) {
  const grouped = new Map();

  for (const item of items) {
    const courseKey = settings?.coursing ? Number(item.course ?? 0) : 0;

    const categoryKey = settings?.group_by_category
      ? safeDecode(item?.Category || "Items").trim() || "Items"
      : "Items";

    if (!grouped.has(courseKey)) {
      grouped.set(courseKey, new Map());
    }

    const courseMap = grouped.get(courseKey);

    if (!courseMap.has(categoryKey)) {
      courseMap.set(categoryKey, []);
    }

    courseMap.get(categoryKey).push(item);
  }

  return grouped;
}

function pushHeader(lines, order, settings = {}) {
  const totalItems = getVisiblePrintItems(order?.items || [], settings).length;

  lines.push(strongDivider);
  lines.push(centerText("MYODS KITCHEN"));
  lines.push(centerText("TICKET"));
  lines.push(strongDivider);
  lines.push(centerText(`ORDER #${order?.order_id ?? ""}`));
  lines.push(divider);

  if (order?.table_name) {
    lines.push(clampLine(`Table: ${safeDecode(order.table_name)}`));
  }

  if (order?.server_name) {
    lines.push(clampLine(`Server: ${safeDecode(order.server_name)}`));
  }

  if (order?.destination) {
    lines.push(clampLine(`Destination: ${safeDecode(order.destination)}`));
  }

  if (order?.user_info) {
    lines.push(clampLine(`Guest: ${safeDecode(order.user_info)}`));
  }

  if (order?.covers) {
    lines.push(clampLine(`Covers: ${safeDecode(String(order.covers))}`));
  }

  const createdAt = formatTime(order?.createdAt);
  if (createdAt) {
    lines.push(clampLine(`Time: ${createdAt}`));
  }

  lines.push(clampLine(`Items: ${totalItems}`));

  if (order?.isRush) {
    lines.push("");
    lines.push(centerText("!!! RUSH ORDER !!!"));
  }

  lines.push(divider);

  if (Array.isArray(order?.order_messages) && order.order_messages.length > 0) {
    lines.push(centerText("ORDER NOTES"));
    lines.push(divider);

    order.order_messages.forEach((msg) => {
      const text = safeDecode(msg).trim();
      if (text) lines.push(clampLine(`- ${text}`));
    });

    lines.push(divider);
  }
}

function pushCourseHeader(lines, courseIndex, grouped, settings = {}) {
  if (!settings?.coursing) return;

  const totalCourses = [...grouped.keys()].length;
  const label =
    settings?.course_labels?.[String(courseIndex + 1)] ||
    `Course ${courseIndex + 1}`;

  lines.push("");
  lines.push(centerText(String(label).toUpperCase()));
  lines.push(centerText(`(${courseIndex + 1} OF ${totalCourses})`));
  lines.push(divider);
}

function pushCategoryHeader(lines, category, settings = {}) {
  if (!settings?.group_by_category || !category) return;
  lines.push(centerText(String(category).toUpperCase()));
  lines.push(divider);
}

function pushWrappedLine(lines, text, indent = "") {
  const fullText = String(text);
  const width = LINE_WIDTH - indent.length;

  if (fullText.length <= width) {
    lines.push(`${indent}${fullText}`);
    return;
  }

  let remaining = fullText.trim();

  while (remaining.length > 0) {
    if (remaining.length <= width) {
      lines.push(`${indent}${remaining}`);
      break;
    }

    let slice = remaining.slice(0, width);
    const lastSpace = slice.lastIndexOf(" ");

    if (lastSpace > 10) {
      slice = slice.slice(0, lastSpace);
    }

    lines.push(`${indent}${slice.trimEnd()}`);
    remaining = remaining.slice(slice.length).trimStart();
  }
}

function pushItem(lines, item, settings = {}) {
  const qty = Number(item?.Quantity || 1);
  const name = safeDecode(item?.Name || "Unnamed Item").trim() || "Unnamed Item";
  const seat = formatSeat(item);

  const prefix = item?.showsAddon ? "[ADD-ON] " : "";
  const mainLine = `${prefix}x${qty} ${name}${seat ? ` ${seat}` : ""}`;

  pushWrappedLine(lines, mainLine);

  const modifiers = getItemModifiers(item, settings);
  modifiers.forEach((mod) => {
    pushWrappedLine(lines, `- ${safeDecode(mod)}`, "   ");
  });

  lines.push("");
}

export function buildPrintTicket(order, settings = {}) {
  const lines = [];
  const items = getVisiblePrintItems(order?.items || [], settings);
  const grouped = groupItems(items, settings);

  pushHeader(lines, order, settings);

  const sortedCourses = [...grouped.entries()].sort(([a], [b]) => a - b);

  for (const [courseIndex, categoryMap] of sortedCourses) {
    pushCourseHeader(lines, courseIndex, grouped, settings);

    for (const [category, categoryItems] of categoryMap.entries()) {
      pushCategoryHeader(lines, category, settings);

      for (const item of categoryItems) {
        pushItem(lines, item, settings);
      }
    }
  }

  lines.push(divider);
  lines.push(centerText(`TOTAL ITEMS: ${items.length}`));
  lines.push(strongDivider);
  lines.push("");
  lines.push("");

  return lines;
}

export default buildPrintTicket;
